import { ReactElement } from 'react';

import Stack from '@mui/material/Stack';

import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonth';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DateRangeIcon from '@mui/icons-material/DateRange';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import Groups2Icon from '@mui/icons-material/Groups2';
import LanguageIcon from '@mui/icons-material/Language';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonIcon from '@mui/icons-material/Person';
import TagIcon from '@mui/icons-material/Tag';
import WineBarIcon from '@mui/icons-material/WineBar';

import League from '../../types/League';
import Season from '../../types/Season';
import { CompetitiveLevels } from '../../utils/Compete';
import { GratiCardDetail } from '../common/GratiCard';

export { getCurrentSeason, getCurrentSeasonName } from '../../utils/seasonUtils';

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * Get category text (org name)
 */
export function getLeagueCategory(league: League): string {
  return league.orgName || '';
}

/**
 * Get competition level detail with trophy icons × level
 */
export function getLeagueCompetitionDetail(league: League): GratiCardDetail {
  const level = league.competitionLevel ?? 0;
  const icon: ReactElement = level > 0 ? (
    <Stack direction="row" spacing={0}>
      {Array.from({ length: level }, (_, i) => (
        <EmojiEventsIcon key={i} sx={{ fontSize: '1rem' }} />
      ))}
    </Stack>
  ) : (
    <WineBarIcon sx={{ fontSize: '1rem' }} />
  );

  return {
    icon,
    text: level === 0 ? 'Social' : CompetitiveLevels[level],
  };
}

/**
 * Get play days detail from defaultPlayDays number[]
 */
export function getLeaguePlayDaysDetail(league: League): GratiCardDetail | undefined {
  if (!league.defaultPlayDays || league.defaultPlayDays.length === 0) return undefined;

  const dayNames = league.defaultPlayDays
    .sort((a, b) => a - b)
    .map(d => DAYS_OF_WEEK[d])
    .join(', ');

  return {
    icon: <DateRangeIcon sx={{ fontSize: '1rem' }} />,
    preText: 'Typically on',
    text: dayNames,
  };
}

/**
 * Get format detail (Team vs Individual)
 */
export function getLeagueFormatDetail(league: League): GratiCardDetail {
  return {
    icon: league.isTeam
      ? <Groups2Icon sx={{ fontSize: '1rem' }} />
      : <PersonIcon sx={{ fontSize: '1rem' }} />,
    text: league.isTeam ? 'Team ' : 'Individual ',
    postText: 'play',
  };
}

/**
 * Get current season detail
 */
export function getLeagueSeasonDetail(seasonName: string): GratiCardDetail {
  return {
    icon: <CalendarViewMonthIcon sx={{ fontSize: '1rem' }} />,
    preText: 'Current season:',
    text: seasonName,
  };
}

/**
 * Build all league details for GratiCard
 */
export function getLeagueDetails(league: League, currentSeason?: Season, onGgidCopy?: () => void, onGgPortalOpen?: () => void): GratiCardDetail[] {
  const details: GratiCardDetail[] = [];

  details.push(getLeagueCompetitionDetail(league));

  const playDays = getLeaguePlayDaysDetail(league);
  if (playDays) {
    details.push(playDays);
  }

  details.push(getLeagueFormatDetail(league));

  if (currentSeason?.name) {
    details.push(getLeagueSeasonDetail(currentSeason.name));
  }

  if (currentSeason?.ggid) {
    details.push({ icon: <TagIcon sx={{ fontSize: '1rem' }} />, preText: 'GGID:', text: currentSeason.ggid, actionIcon: <ContentCopyIcon sx={{ fontSize: '1rem' }} />, actionHandler: onGgidCopy });
  }

  if (currentSeason?.ggPortal) {
    details.push({ icon: <LanguageIcon sx={{ fontSize: '1rem' }} />, preText: 'Portal:', text: 'Golf Genius', actionIcon: <OpenInNewIcon sx={{ fontSize: '1rem' }} />, actionHandler: onGgPortalOpen });
  }

  return details;
}
